import { useState } from 'react';
import { COLLECT_LOGS_SETTING_IDS } from '../../config/SettingIds';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { LogCollectionDialogMode } from './LogCollectionDialog';
import { LogFileInfo } from './LogFileCard';

const parseLogFileFromResponse = (
	settingValue: string | undefined
): LogFileInfo | null => {
	if (!settingValue) return null;
	try {
		const parsed = JSON.parse(settingValue);
		if (parsed.fileName && parsed.fileSize && parsed.createdAt) {
			return parsed as LogFileInfo;
		}
	} catch {
		// not a log file response
	}
	return null;
};

export interface UseLogCollectionReturn {
	dialogMode: LogCollectionDialogMode | null;
	logFile: LogFileInfo | null;
	handleStartCollecting: (logLevel: string) => Promise<void>;
	handleCollectExisting: () => Promise<void>;
	handleStop: () => Promise<void>;
	handleCancel: () => Promise<void>;
	handleDownload: () => Promise<void>;
	handleDelete: () => Promise<void>;
}

export const useLogCollection = (): UseLogCollectionReturn => {
	const [dialogMode, setDialogMode] = useState<LogCollectionDialogMode | null>(null);
	const [logFile, setLogFile] = useState<LogFileInfo | null>(null);

	const handleStartCollecting = async (logLevel: string) => {
		setDialogMode('capturing');
		const bridge = SettingsNativeBridgeFactory.getBridge();
		await bridge.setSetting({
			settingKey: COLLECT_LOGS_SETTING_IDS.START_COLLECTING,
			settingValue: JSON.stringify({ Action: 'Start Collecting', LogLevel: logLevel }),
		});
	};

	const handleCollectExisting = async () => {
		setDialogMode('collecting');
		const bridge = SettingsNativeBridgeFactory.getBridge();
		const response = await bridge.setSetting({
			settingKey: COLLECT_LOGS_SETTING_IDS.COLLECT_EXISTING,
			settingValue: true,
		});
		setLogFile(parseLogFileFromResponse(response?.data?.settingValue));
		setDialogMode(null);
	};

	const handleStop = async () => {
		const bridge = SettingsNativeBridgeFactory.getBridge();
		const response = await bridge.setSetting({
			settingKey: COLLECT_LOGS_SETTING_IDS.STOP_COLLECTING,
			settingValue: true,
		});
		setLogFile(parseLogFileFromResponse(response?.data?.settingValue));
		setDialogMode(null);
	};

	const handleCancel = async () => {
		const bridge = SettingsNativeBridgeFactory.getBridge();
		await bridge.setSetting({
			settingKey: COLLECT_LOGS_SETTING_IDS.CANCEL_COLLECTING,
			settingValue: true,
		});
		setDialogMode(null);
	};

	const handleDownload = async () => {
		if (!logFile) return;
		const bridge = SettingsNativeBridgeFactory.getBridge();
		await bridge.setSetting({
			settingKey: COLLECT_LOGS_SETTING_IDS.DOWNLOAD_LOG,
			settingValue: logFile.fileName,
		});
	};

	const handleDelete = async () => {
		if (!logFile) return;
		const bridge = SettingsNativeBridgeFactory.getBridge();
		await bridge.setSetting({
			settingKey: COLLECT_LOGS_SETTING_IDS.DELETE_LOG,
			settingValue: logFile.fileName,
		});
		setLogFile(null);
	};

	return {
		dialogMode,
		logFile,
		handleStartCollecting,
		handleCollectExisting,
		handleStop,
		handleCancel,
		handleDownload,
		handleDelete,
	};
};
